import React, {
    useEffect,
    useState,
    useRef,
} from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { parseISO, format } from 'date-fns';
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.css";

import * as UTILS from "../utils";
import * as QUERIES from "../query-functions";
import * as CONSTANTS from "../constants";
import CalendarIcon from '../svg/calendar';
import PreviousIcon from '../svg/previous';
import NextIcon from '../svg/next';

export default function ViewAlternativeDates({ selectedInstanceDate }) {

    const dialogRef = useRef(null);
    const inputRef = useRef(null);
    const pickerRef = useRef(null);

    const [enabledDates, setEnabledDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [performances, setPerformances] = useState(null);
    const [selectedPerformance, setSelectedPerformance] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    const { data: eventInstancesData } = useQuery({
        queryKey: ['eventInstancesData'],
        queryFn: () => QUERIES.getEventInstances(),
        staleTime: 86400000,
        refetchOnWindowFocus: false,
    });

    const handleSelectPerformance = (e) => {
        if (!e) return;

        const selected_performance = performances.find((perf) => perf.id == e.target.value);
        setSelectedPerformance(selected_performance);
    }

    useEffect(() => {
        if (inputRef.current && !pickerRef.current && enabledDates) {
            pickerRef.current = flatpickr(inputRef.current, {
                inline: true,
                minDate: "today",
                monthSelectorType: "static",
                locale: {
                    firstDayOfWeek: 1,
                },
                prevArrow:
                    '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16" fill="none"><path d="M9.53082 15.5567C8.90525 16.1478 7.89099 16.1478 7.26542 15.5567L0.469225 9.13561C-0.156347 8.54457 -0.156347 7.5863 0.469225 6.99525C1.0948 6.4042 2.10905 6.4042 2.73462 6.99525L9.53082 13.4163C10.1564 14.0074 10.1564 14.9657 9.53082 15.5567Z" fill="black"/><path d="M9.53077 0.443284C10.1563 1.03433 10.1563 1.9926 9.53077 2.58365L2.73458 9.00475C2.109 9.5958 1.09475 9.5958 0.469179 9.00475C-0.156395 8.41371 -0.156395 7.45543 0.469178 6.86438L7.26537 0.443285C7.89095 -0.147761 8.9052 -0.147761 9.53077 0.443284Z" fill="black"/></svg>',
                nextArrow:
                    '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16" fill="none"><path d="M0.469179 15.5567C1.09475 16.1478 2.10901 16.1478 2.73458 15.5567L9.53077 9.13561C10.1563 8.54457 10.1563 7.5863 9.53077 6.99525C8.9052 6.4042 7.89095 6.4042 7.26538 6.99525L0.469179 13.4163C-0.156394 14.0074 -0.156394 14.9657 0.469179 15.5567Z" fill="black"/><path d="M0.469226 0.443284C-0.156347 1.03433 -0.156347 1.9926 0.469226 2.58365L7.26542 9.00475C7.891 9.5958 8.90525 9.5958 9.53082 9.00475C10.1564 8.41371 10.1564 7.45543 9.53082 6.86438L2.73463 0.443285C2.10905 -0.147761 1.0948 -0.147761 0.469226 0.443284Z" fill="black"/></svg>',
                enableTime: false,
                dateFormat: "Y-m-d",
                enable: enabledDates,
                // defaultDate: enabledDates[0],
                defaultDate: selectedInstanceDate,
                onChange: function (selectedDate, dateStr, instance) {
                    setSelectedDate(dateStr);
                },
            });

            setSelectedDate(enabledDates[0]);
        }

        return () => {
            if (pickerRef.current) {
                pickerRef.current.destroy();
                pickerRef.current = null;
            }
        };
    }, [inputRef.current, enabledDates]);

    useEffect(() => {
        if (!eventInstancesData) return;

        const enabled_dates = UTILS.getUniqueDates(eventInstancesData);
        setEnabledDates(enabled_dates);
        
    }, [eventInstancesData]);

    useEffect(() => {
        if (!selectedDate || !eventInstancesData) return;

        const performances = eventInstancesData
        .filter((instance) => {
            const date = instance.start.slice(0, 10);
            return date === selectedDate;
        })
        .map((instance) => {
            return {
                ...instance,
                start_date: instance.start.slice(0, 10),
                start_time: format(parseISO(instance.start), "h:mma"),
                iframe_id: instance.id.match(/^\d+/)
            };
        });
        
        setPerformances(performances);
        setSelectedPerformance(performances[0]);
    }, [selectedDate]);

    // console.log('SELECTED', selectedDate)

    // Sync currentIndex based on selectedInstanceDate
    useEffect(() => {
        if (!Array.isArray(enabledDates) || !selectedInstanceDate) return;

        const dateOnly = selectedInstanceDate.split(" ")[0];
        const index = enabledDates.indexOf(dateOnly);
        setCurrentIndex(index !== -1 ? index : 0);
    }, [selectedInstanceDate, enabledDates]);

    function getInstancesByDate(instances, date) {
        return instances.filter(inst => inst.start.slice(0, 10) === date);
    }

    const currentDate = enabledDates[currentIndex];
    const instances = currentDate ? getInstancesByDate(eventInstancesData, currentDate) : [];
    const currentId = window.location.pathname.match(/\d+/);
    const currentInstanceIndex = instances.findIndex(inst => inst.id.includes(currentId));

    const goToInstance = (inst) => {
        if (inst) {
            const id = inst.id.match(/^\d+/);
            window.location.href = `/book-online/${id}`;
        }
    };

    const handlePrev = () => {
        if (currentInstanceIndex > 0) {
            goToInstance(instances[currentInstanceIndex - 1]);
        } else if (currentIndex > 0) {
            const prevInstances = getInstancesByDate(eventInstancesData, enabledDates[currentIndex - 1]);
            goToInstance(prevInstances[prevInstances.length - 1]);
        }
    };

    const handleNext = () => {
        if (currentInstanceIndex < instances.length - 1) {
            goToInstance(instances[currentInstanceIndex + 1]);
        } else if (currentIndex < enabledDates.length - 1) {
            const nextInstances = getInstancesByDate(eventInstancesData, enabledDates[currentIndex + 1]);
            goToInstance(nextInstances[0]);
        }
    };

    // Buttons disable state
    const isPrevDisabled = currentIndex === 0 && currentInstanceIndex === 0;
    const isNextDisabled = currentIndex === enabledDates.length - 1 && currentInstanceIndex === instances.length - 1;

    if (!eventInstancesData?.length || eventInstancesData?.length < 2) return null;

    return (
        <div className="view-alternative-dates">
            <button type="button" 
                    className='view-alternative-dates__button' 
                    onClick={() => {dialogRef.current.showModal(); pickerRef.current?.open();}}>
                <CalendarIcon />
                View calendar
            </button>
            <div className='view-alternative-dates__paginate'>
                <button
                    type="button"
                    className="view-alternative-dates__button"
                    onClick={handlePrev}
                    disabled={isPrevDisabled}>
                    <PreviousIcon />
                    Previous date
                </button>

                <button
                    type="button"
                    className="view-alternative-dates__button"
                    onClick={handleNext}
                    disabled={isNextDisabled}>
                    <NextIcon />
                    Next date
                </button>
            </div>

            <dialog ref={dialogRef} className='view-alternative-dates__dialog'>
                <div className='dialog-container'>
                    <button type="button" className='btn--cross btn--dialog-close' onClick={() => dialogRef.current.close()}>
                        <span className='sr-text'>Close dialog</span>
                        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="36" y="23" width="2" height="24" transform="rotate(90 36 23)"/>
                            <rect x="23" y="12" width="2" height="24" />
                        </svg>
                    </button>

                    <div className='dialog-content'>
                        <h1 className='booking-pathway__header'>Select a date</h1>

                        <div className='date-picker'>
                            <input className='view-alternative-dates__picker-input' ref={inputRef} type="text" placeholder="Select Date" />
                        </div>

                        {performances?.length ? (
                            <div className='view-alternative-dates__performances'>
                                <label htmlFor="performances">Choose a performance</label>
                                <select id="performances" 
                                        name="performances" 
                                        onChange={handleSelectPerformance}
                                        defaultValue={selectedPerformance.id}>
                                    {performances.map((performance) => (
                                        <option key={performance.id} value={performance.id}>
                                            {performance.start_time}
                                        </option>
                                    ))}
                                </select>

                                {selectedPerformance &&
                                    <a href={`/book-online/${selectedPerformance.iframe_id}`} className='btn view-alternative-dates__link'>
                                        Apply
                                    </a>
                                }
                            </div>
                        ) : null}
                    </div>
                </div>

            </dialog>
        </div>
    );
}